import React from 'react';
import { SpecChip, chipTextOf } from './specChipModel';

/**
 * "Specs found": the line's own spec, then each alternative spec research
 * found. Choosing a chip shows that spec's suppliers in the list below; the
 * line's own spec is chosen by default. Each chip is a toggle button
 * (aria-pressed), so the row works from the keyboard.
 */
interface SpecChipsProps {
  chips: SpecChip[];
  selected: string;
  onSelect: (specGroup: string) => void;
}

const chipClass = (selected: boolean) =>
  'inline-flex h-7 items-center gap-[5px] whitespace-nowrap rounded-round border px-s4 font-sans text-body-sm ' +
  'transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 ' +
  (selected
    ? 'border-action bg-action-bg text-action-deep'
    : 'border-line-strong bg-surface text-ink-secondary hover:border-action hover:text-ink');

export const SpecChips: React.FC<SpecChipsProps> = ({ chips, selected, onSelect }) => {
  if (chips.length === 0) return null;
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-s3 gap-y-s2 px-[2px]" role="group" aria-label="Specs found">
      <span className="text-table-header uppercase text-ink-muted">Specs found</span>
      {chips.map((chip) => {
        const parts = chipTextOf(chip);
        const [label, ...rest] = parts;
        const isSelected = chip.specGroup === selected;
        return (
          <button
            key={chip.specGroup || 'line'}
            type="button"
            aria-pressed={isSelected}
            aria-label={parts.join(' · ')}
            onClick={() => onSelect(chip.specGroup)}
            className={chipClass(isSelected)}
          >
            <span className={`font-semibold ${isSelected ? 'text-action-deep' : 'text-ink'}`}>{label}</span>
            {rest.map((part) => (
              <React.Fragment key={part}>
                <span aria-hidden className="text-ink-muted">
                  ·
                </span>
                <span>{part}</span>
              </React.Fragment>
            ))}
          </button>
        );
      })}
    </div>
  );
};

export default SpecChips;
